import React from 'react'
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Link,
  Button,
  LinearProgress,
  CircularProgress,
  FormControl,
  SvgIcon,
  Chip,
  linearProgressClasses,
} from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  elGR,
  enUS,
  getGridStringOperators,
  GridSortModel,
} from '@mui/x-data-grid'
import {
  Link as RouterLink,
  Outlet,
  LinkProps,
  useSearchParams,
} from 'react-router-dom'
import { createTheme, styled, ThemeProvider } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import NoResultsOverlay from 'shared/table/overlays/NoResults'
// import ErrorOverlay from 'shared/table/overlays/Error'
import AgentService from 'api/services/Agent'
import { SearchAgentsResponse } from 'api/services/Agent/types'
import CustomPagination from 'shared/table/Pagination'
import DeleteDialog from './DeleteDialog'
import { EstateAgentBasicInfoDTO } from 'types'

import { PropertyAdService } from 'api/services/PropertyAd'
import { useMutation, useQueryClient } from 'react-query'
import SharedBackdrop from 'components/shared/SharedBackdrop'
import StatsDialog from './StatsDialog'
import { CustomSwitch } from 'components/AgentProperties'
import { ReactComponent as SpourgitiSvg } from 'assets/svg/icons/lists/g-spourgiti.svg'
import { ReactComponent as HousesSvg } from 'assets/svg/new-icons/houses.svg'
import { ReactComponent as CircledDotsSvg } from 'assets/svg/new-icons/circled-dots.svg'
import { ReactComponent as TrashcanSvg } from 'assets/svg/new-icons/trashcan.svg'
import { ReactComponent as PhoneSvg } from 'assets/svg/new-icons/phone.svg'
import { ReactComponent as MailSvg } from 'assets/svg/new-icons/mail.svg'
import { FilterToAdd } from './types'
import FilterInput from './FilterInput'
import { AgentStatusEnum } from 'types/enums'
import { AGENT_SOURCE_OPTIONS } from './constants'
import DateFormatterService from 'services/DateFormatterService'

const LinkButton = styled(Button)<LinkProps>(() => ({}))

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 24,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor:
      theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.primary.main,
  },
}))

const theme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '15px',
          color: 'black',
          backgroundColor: '#ffffff',
        },
      },
    },
  },
})

const Agents: React.FC = () => {
  const { t, i18n } = useTranslation()

  const queryClient = useQueryClient()

  const IS_LOCALE_GR = i18n.language === 'gr'

  const { enqueueSnackbar } = useSnackbar()

  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false)
  const [selectedAgent, setSelectedAgent] = React.useState<
    EstateAgentBasicInfoDTO | undefined
  >()

  const handleClickOpenDeleteDialog = () => {
    setOpenDeleteDialog(true)
  }

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false)
  }

  const [searchParams] = useSearchParams()
  const HAS_PARAM = searchParams.get('page')

  const [page, setPage] = React.useState(() =>
    HAS_PARAM ? Number(searchParams.get('page')) - 1 : 0
  )
  const [pageSize] = React.useState(25)
  const [results, setResults] = React.useState([] as SearchAgentsResponse)
  const [rowCountState, setRowCountState] = React.useState(0)

  const [filterValue, setFilterValue] = React.useState<object | undefined>()
  const [searchCriteria, setSearchCriteria] = React.useState<FilterToAdd[]>([])

  const [sortValue, setSortValue] = React.useState<object | undefined>()
  const [, setOpen] = React.useState(false)
  const [openDialog, setOpenDialog] = React.useState(false)

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpenDialog = () => {
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
  }

  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      if (sortModel.length) {
        const model: { sortingField?: string; sortingOrder?: string } = {}
        switch (sortModel[0].field) {
          case 'source':
            model.sortingField = sortModel[0]?.field?.toUpperCase()
            model.sortingOrder = sortModel[0]?.sort?.toUpperCase()
            break
          case 'companyName':
            model.sortingField = 'COMPANY_NAME'
            model.sortingOrder = sortModel[0]?.sort?.toUpperCase()
            break
          case 'propertyAds':
            model.sortingField = 'TOTAL_PROPERTY_ADS'
            model.sortingOrder = sortModel[0]?.sort?.toUpperCase()
            break
          case 'lastUpdate':
            model.sortingField = 'LAST_UPDATE_DATE'
            model.sortingOrder = sortModel[0]?.sort?.toUpperCase()
            break
          default:
            break
        }
        setSortValue(model)
        setPage(0)
      } else {
        setSortValue({})
      }
    },
    []
  )

  const countAgents = AgentService.hooks.useCountAgents(filterValue, {
    onSuccess(data) {
      if (!data) setResults([])
    },
  })

  const countPropertyAdMutation = PropertyAdService.hooks.useCountPropertyAd({})

  React.useEffect(() => {
    setRowCountState((prevRowCountState) =>
      countAgents?.data !== undefined
        ? (countAgents?.data as number)
        : prevRowCountState
    )
  }, [countAgents.data, setRowCountState])

  const agents = AgentService.hooks.useAgents(
    page,
    pageSize,
    filterValue,
    sortValue,
    {
      enabled: countAgents.isSuccess && !!countAgents.data,
      onError: () => {
        enqueueSnackbar(t('tables.responses.error') as unknown as string, {
          variant: 'error',
        })
      },
    }
  )

  React.useEffect(() => {
    setResults((prevState) => (agents?.data !== undefined ? agents.data : []))
  }, [agents.data, setResults])

  const handlePageChange = (newPage) => {
    setPage(newPage)
  }

  const deleteAgentMutation = AgentService.hooks.useDeleteAgent({
    onSuccess: () => {
      enqueueSnackbar(t('tables.agents.dialog.delete.messages.success'), {
        variant: 'success',
      })
      setSelectedAgent(undefined)
      handleCloseDeleteDialog()
      // 'agents'
      queryClient.invalidateQueries(AgentService.keys.QueryKeys.all)
    },
    onError: (err) => {
      enqueueSnackbar(t('tables.responses.error') as unknown as string, {
        variant: 'error',
      })
    },
  })

  const handleDeleteRow = (row: SearchAgentsResponse[number]) => {
    handleClickOpenDeleteDialog()
    setSelectedAgent(row)
  }

  const handleSubmitDelete = () => {
    if (selectedAgent?.id) {
      deleteAgentMutation.mutate(selectedAgent.id)
    }
  }

  const handleCloseDelete = () => {
    setSelectedAgent(undefined)
    handleCloseDeleteDialog()
  }

  const getAgentStatistics = async (payload) => {
    const activeProperties = countPropertyAdMutation.mutateAsync({
      propertyAdStatus: 'ACTIVE',
      ...payload,
    })

    const inactiveProperties = countPropertyAdMutation.mutateAsync({
      propertyAdStatus: 'INACTIVE',
      ...payload,
    })

    const deletedProperties = countPropertyAdMutation.mutateAsync({
      propertyAdStatus: 'DELETED',
      ...payload,
    })

    const invalidProperties = countPropertyAdMutation.mutateAsync({
      propertyAdStatus: 'INVALID',
      ...payload,
    })

    const data = await Promise.all([
      activeProperties,
      inactiveProperties,
      deletedProperties,
      invalidProperties,
    ]).then((data) =>
      data.reduce((acc, item, index) => {
        if (index === 0) {
          acc['ACTIVE'] = item ?? 0
        } else if (index === 1) {
          acc['INACTIVE'] = item ?? 0
        } else if (index === 2) {
          acc['DELETED'] = item ?? 0
        } else {
          acc['INVALID'] = item ?? 0
        }
        return acc
      }, {})
    )

    return data
  }

  const getAgentStatsMutation = useMutation(getAgentStatistics, {
    onSuccess: (data) => {
      handleOpenDialog()
    },
    onError: (error) => {
      enqueueSnackbar(t('error.unspecific'), {
        variant: 'error',
      })
    },
  })

  const handleStatistics = () => {
    getAgentStatsMutation.mutate({})
  }

  const handleClickOnCopy = (type, textToCopy) => {
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy)
      enqueueSnackbar(
        type === 'mobile'
          ? t('tables.agents.copy.phoneSuccess')
          : t('tables.agents.copy.mailSuccess'),
        {
          variant: 'success',
        }
      )
    }
  }

  const updateAgentStatus = AgentService.hooks.useUpdateAgentStatus({
    onSuccess: () => {
      countAgents.refetch()
      agents.refetch()
      enqueueSnackbar(t('tables.agents.updateStatus.success'), {
        variant: 'success',
      })
    },
    onError: (err) => {
      enqueueSnackbar(t('error.unspecific'), {
        variant: 'error',
      })
    },
  })

  const handleChangeAgentStatus = (agent) => {
    updateAgentStatus.mutate({
      id: agent.id,
      status: agent.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
    })
  }

  const columns: GridColDef[] = [
    {
      field: 'status',
      flex: 1,
      headerName: t('tables.agents.columns.status'),
      maxWidth: 105,
      minWidth: 105,
      headerClassName: 'table--header',
      type: 'singleSelect',
      hideable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            <FormControl variant="standard" sx={{}}>
              <CustomSwitch
                status={params.row.status}
                checked={['ACTIVE'].includes(params.row.status)}
                disabled={
                  ['INVALID'].includes(params.row.status) ||
                  updateAgentStatus.isLoading
                }
                onChange={() => handleChangeAgentStatus(params?.row)}
              />
            </FormControl>
          </>
        )
      },
    },
    {
      field: 'companyName',
      headerName: t('tables.agents.columns.name'),
      flex: 1,
      minWidth: 228,
      headerClassName: 'table--header',
      hideable: false,
      sortable: false,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === 'contains'
      ),
      renderCell: (params) => {
        return (
          <ThemeProvider theme={theme}>
            <Tooltip
              title={
                params?.row?.companyTitle
                  ? params?.row?.companyTitle
                  : ('-' as string)
              }
            >
              <Box sx={{ textOverflow: 'ellipsis' }}>
                {params?.row?.companyTitle}
              </Box>
            </Tooltip>
          </ThemeProvider>
        )
      },
    },
    {
      field: 'source',
      headerName: t('tables.agents.columns.source'),
      flex: 1,
      minWidth: 150,
      maxWidth: 150,
      headerClassName: 'table--header',
      hideable: false,
      sortable: true,
      type: 'singleSelect',
      valueOptions: [
        ...AGENT_SOURCE_OPTIONS.map((item) => ({
          value: item.value,
          label: t(item.label),
        })),
      ],
      valueFormatter: (params) => {
        return params?.value ? t(`enums.sources.${params?.value}.base`) : '-'
      },
    },
    {
      field: 'externalId',
      headerName: t('tables.agents.columns.externalId'),
      flex: 0.7,
      minWidth: 100,
      maxWidth: 135,
      headerClassName: 'table--header',
      hideable: false,
      sortable: false,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === 'equals'
      ),
      valueGetter: (params) => {
        return params?.row?.externalId ? params.row.externalId : '-'
      },
    },
    {
      field: 'propertyAds',
      flex: 1,
      headerName: t('tables.agents.columns.propertyAds'),
      minWidth: 110,
      maxWidth: 135,
      headerClassName: 'table--header',
      hideable: false,
      filterable: false,
      sortable: true,
      renderCell: (params) => {
        const calculate = (params) => {
          const totals = { ACTIVE: 0, INACTIVE: 0, INVALID: 0, ALL: 0 }
          if (params.row?.propertyAdsTotal) {
            params.row.propertyAdsTotal.forEach((pat) => {
              if (pat.propertyAdStatus === AgentStatusEnum.Active) {
                totals.ACTIVE = pat.total
              } else if (pat.propertyAdStatus === AgentStatusEnum.Inactive) {
                totals.INACTIVE = pat.total
              } else if (pat.propertyAdStatus === AgentStatusEnum.Invalid) {
                totals.INVALID = pat.total
              }
              totals.ALL += pat.total
            })
          }
          return totals
        }

        const constructText = (totals) => {
          return `${t(
            'tables.agents.status.active'
          )}: ${totals.ACTIVE.toLocaleString('el-GR')} \n
          ${t(
            'tables.agents.status.inactive'
          )}: ${totals.INACTIVE.toLocaleString('el-GR')} \n
          ${t('tables.agents.status.invalid')}: ${totals.INVALID.toLocaleString(
            'el-GR'
          )} \n`
        }
        const totals = calculate(params)
        return (
          <ThemeProvider theme={theme}>
            <Tooltip
              title={
                <Box sx={{ whiteSpace: 'pre-line', lineHeight: '13px' }}>
                  {constructText(totals)}
                </Box>
              }
            >
              <Typography>{`${totals.ACTIVE.toLocaleString(
                'el-GR'
              )}/${totals.ALL.toLocaleString('el-GR')}`}</Typography>
            </Tooltip>
          </ThemeProvider>
        )
      },
    },
    {
      field: 'lastUpdate',
      flex: 1,
      headerName: t('tables.agents.columns.lastUpdate'),
      minWidth: 110,
      headerClassName: 'table--header',
      hideable: false,
      filterable: false,
      sortable: true,
      renderCell: (params) => {
        return (
          <Typography>
            {params?.row?.lastUpdateDate
              ? DateFormatterService.formatDate(params.row.lastUpdateDate)
              : '-'}
          </Typography>
        )
      },
    },
    {
      field: 'actions',
      flex: 1,
      headerClassName: 'table--header',
      type: 'actions',
      minWidth: 280,
      hideable: false,
      filterable: false,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const calculateActive = (params) => {
          let activeTotal = 0
          if (params.row?.propertyAdsTotal) {
            params.row.propertyAdsTotal.forEach((pat) => {
              if (pat.propertyAdStatus === AgentStatusEnum.Active) {
                activeTotal = pat.total
              }
            })
          }
          return activeTotal
        }
        const activeTotal = calculateActive(params)

        return (
          <Stack direction="row" spacing={1}>
            <ThemeProvider theme={theme}>
              <Tooltip
                title={
                  params?.row?.mobileNumber
                    ? params?.row?.mobileNumber
                    : ('-' as string)
                }
              >
                <IconButton
                  onClick={() => {
                    handleClickOnCopy('mobile', params.row?.mobileNumber)
                  }}
                >
                  <SvgIcon
                    component={PhoneSvg}
                    inheritViewBox
                    sx={{ color: 'black' }}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  params?.row?.email
                    ? params?.row?.email
                    : ('-' as unknown as string)
                }
              >
                <IconButton
                  onClick={() => {
                    handleClickOnCopy('email', params.row?.email)
                  }}
                >
                  <SvgIcon
                    component={MailSvg}
                    inheritViewBox
                    sx={{ color: '#ffffff' }}
                  />
                </IconButton>
              </Tooltip>
            </ThemeProvider>
            <Tooltip
              title={
                t(
                  'tables.agents.columns.actions.properties'
                ) as unknown as string
              }
            >
              <Link
                component={RouterLink}
                to={`/agent-properties/${params.id}`}
                underline="none"
              >
                <IconButton>
                  <SvgIcon
                    component={HousesSvg}
                    inheritViewBox
                    sx={{ color: 'black' }}
                  />
                </IconButton>
              </Link>
            </Tooltip>
            {params.row.status === AgentStatusEnum.Active ? (
              <Tooltip
                title={
                  t(
                    'tables.agents.columns.actions.external'
                  ) as unknown as string
                }
              >
                <Link
                  href={`${process.env.REACT_APP_REDIRECT_SEARCH_APP_URL}estate-agents/el:${params.row?.alternativeId}`}
                  underline="none"
                  target="_blank"
                >
                  <IconButton>
                    <SvgIcon
                      component={SpourgitiSvg}
                      inheritViewBox
                      sx={{ color: 'black' }}
                    />
                  </IconButton>
                </Link>
              </Tooltip>
            ) : (
              <Tooltip
                title={
                  t(
                    'tables.agents.columns.actions.inactiveAgent'
                  ) as unknown as string
                }
              >
                <span>
                  <IconButton
                    disabled={params.row.status !== AgentStatusEnum.Active}
                  >
                    <SvgIcon component={SpourgitiSvg} inheritViewBox />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip
              title={
                t('tables.agents.columns.actions.edit') as unknown as string
              }
            >
              <Link component={RouterLink} to={params.id + ''} underline="none">
                <IconButton>
                  <SvgIcon
                    component={CircledDotsSvg}
                    inheritViewBox
                    sx={{ color: '#ffffff' }}
                  />
                </IconButton>
              </Link>
            </Tooltip>
            <Tooltip
              title={
                activeTotal > 0
                  ? (t(
                      'tables.agents.columns.actions.canNotDelete'
                    ) as unknown as string)
                  : (t(
                      'tables.agents.columns.actions.delete'
                    ) as unknown as string)
              }
            >
              <span>
                <IconButton
                  onClick={() => handleDeleteRow(params.row)}
                  disabled={activeTotal > 0}
                >
                  <SvgIcon
                    component={TrashcanSvg}
                    inheritViewBox
                    sx={{
                      color: 'transparent',
                      opacity: activeTotal > 0 ? 0.6 : 1,
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        )
      },
    },
  ]

  const handleFilterAddition = (newFilter) => {
    if (filterValue) filterValue[newFilter.type] = newFilter.value
    else setFilterValue({ [newFilter.type]: newFilter.value })
    const searchCriterias = [...searchCriteria]
    const finalCriterias = searchCriterias.filter(
      (sc) => sc.type !== newFilter.type
    )
    finalCriterias.push(newFilter)
    setSearchCriteria(finalCriterias)
  }

  const handleFilterDelete = (criteriaType) => {
    const searchCriterias = JSON.parse(JSON.stringify(searchCriteria))
    const newCriterias = searchCriterias.filter(
      (sc) => sc.type !== criteriaType
    )
    setSearchCriteria(newCriterias)
    const newFilterValue = JSON.parse(JSON.stringify(filterValue))
    delete newFilterValue[criteriaType]
    setFilterValue(newFilterValue)
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        pt: 1,
        pb: 4,
        borderRadius: 2.5,
      }}
    >
      <Stack>
        <Stack
          direction="row"
          sx={{ alignSelf: 'flex-end', my: 1 }}
          spacing={2}
        >
          <LinkButton
            variant="outlined"
            LinkComponent={RouterLink}
            to="create"
            sx={{
              textTransform: 'none',
              color: '#000',
              boxShadow: '3px 3px 8px 0px #0909094D',
              borderRadius: '8px',
              border: '1px solid #EBEBEB',
              px: '32px',
            }}
          >
            {t('tables.agents.addButton')}
          </LinkButton>
          <Button
            variant="outlined"
            sx={{
              textTransform: 'none',
              color: '#000',
              boxShadow: '3px 3px 8px 0px #0909094D',
              borderRadius: '8px',
              border: '1px solid #EBEBEB',
              px: '32px',
            }}
            onClick={handleStatistics}
          >
            {t('statistics.title')}
          </Button>
        </Stack>
        <Stack direction="row">
          <FilterInput onSubmit={handleFilterAddition} />
        </Stack>

        {searchCriteria ? (
          <Box sx={{ mt: 2, ml: '8px' }}>
            {searchCriteria.map((criteria) => {
              let text = t(`tables.agents.filters.${criteria.type}`) + `: `
              if (criteria?.type === 'status') {
                text += t(
                  `tables.agents.agentStatus.${criteria.value.toLowerCase()}`
                )
              } else if (criteria?.type === 'source') {
                text += t(t(`enums.sources.${criteria?.value}.base`))
              } else {
                text += criteria.value
              }

              return (
                <Chip
                  key={criteria.type}
                  label={text}
                  onDelete={() => handleFilterDelete(criteria.type)}
                  sx={{ mr: '8px' }}
                />
              )
            })}
          </Box>
        ) : null}

        <Box
          sx={{
            width: '100%',
            mt: 2,
          }}
        >
          <div style={{ display: 'flex', height: '100%' }}>
            <Box
              sx={{
                flexGrow: 1,
                '& .table--header': {
                  color: 'textPrimary',
                  fontSize: '1rem',
                  fontWeight: 600,
                },
              }}
            >
              <DataGrid
                localeText={
                  IS_LOCALE_GR
                    ? elGR.components.MuiDataGrid.defaultProps.localeText
                    : enUS.components.MuiDataGrid.defaultProps.localeText
                }
                autoHeight
                disableColumnMenu
                disableSelectionOnClick
                disableColumnSelector={true}
                disableColumnFilter={true}
                rows={results}
                rowCount={rowCountState}
                columns={columns}
                page={page}
                onPageChange={handlePageChange}
                pageSize={pageSize}
                rowsPerPageOptions={[25]}
                components={{
                  LoadingOverlay: LinearProgress,
                  NoRowsOverlay: NoResultsOverlay,
                  Pagination: CustomPagination,
                }}
                loading={
                  countAgents.isLoading || agents.isLoading || agents.isFetching
                }
                paginationMode="server"
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-footerContainer': {
                    justifyContent: 'center',
                  },
                  '& .MuiPagination-root': {
                    mt: '40px',
                  },
                }}
              />
            </Box>
          </div>
          <DeleteDialog
            agent={selectedAgent}
            open={openDeleteDialog}
            onClose={handleCloseDelete}
            onSubmit={handleSubmitDelete}
            isLoading={deleteAgentMutation.isLoading}
          />
        </Box>
        <Outlet />
      </Stack>
      <SharedBackdrop
        open={getAgentStatsMutation.isLoading}
        onClose={handleClose}
      >
        <CircularProgress size={24} />
      </SharedBackdrop>
      <StatsDialog
        open={openDialog}
        onClose={handleCloseDialog}
        data={getAgentStatsMutation.data}
      />
    </Box>
  )
}

export default Agents
